import {
  createContext, useCallback, useContext, useEffect, useState
} from 'react'
import { useRestrictionQuery } from '@src/lib/apiClient'
import useGeoFence from '@src/lib/useGeoFence'
import { ConfigContext } from './ConfigContext'

type Coords = {
  latitude: number
  longitude: number
}
export type RestrictionContextType = {
  geofencingUserApproved: boolean
  setGeofencingUserApproved: (a: boolean) => void
  setCoords: (coords: Coords) => void
  restricted: boolean
  showGeoInstructions: boolean
  isLoading: boolean
  netmaskMessage?: string
}

const initialState: RestrictionContextType = {
  geofencingUserApproved: false,
  setGeofencingUserApproved: () => { /* noop */ },
  setCoords: () => { /* noop */ },
  restricted: false,
  showGeoInstructions: false,
  isLoading: true,
}
export const RestrictionContext = createContext<RestrictionContextType>(initialState)

// TODO: generate better instruction/error messages in this context
export const RestrictionContextProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const { configContext: config } = useContext(ConfigContext)
  const [geofencingUserApproved, setGeofencingUserApproved] = useState<boolean>(false)
  const [coords, setCoords] = useState<Coords | undefined>()
  const { data, isError, isLoading: isLoadingQuery } = useRestrictionQuery(coords)

  // Whether or not the tenant has fencing enabled
  const usingFencing = !!(config?.geoFence || config?.netmaskFence)

  //
  const loadingOrRestricted = (!data || data?.restricted || isError || isLoadingQuery)
  const restricted = loadingOrRestricted && usingFencing

  // Whether or not to show the warning about location services
  const showGeoInstructions = !!(!geofencingUserApproved && config?.geoFence)

  //
  const netmaskMessage = config?.netmaskFence?.message

  const isLoading = !config || (isLoadingQuery && usingFencing)

  // Load whether or not the user has seen and approved the geofencing message previously
  useEffect(() => {
    const initialGeofencingApproved = window.localStorage?.getItem('geofencingApproved')
    setGeofencingUserApproved(initialGeofencingApproved === 'true')
  }, [setGeofencingUserApproved])

  const setGeofencingUserApprovedAndCache = useCallback((val: boolean) => {
    setGeofencingUserApproved(val)
    window.localStorage?.setItem('geofencingApproved', val ? 'true' : 'false')
  }, [setGeofencingUserApproved])

  // Redirect the user to https if not so that browser Geolocation API works
  useEffect(() => {
    if (config?.geoFence && window.location.protocol === 'http:' && process.env.NODE_ENV === 'production') {
      // NB: nextjs magically sets process.env.NODE_ENV on the frontend apparently
      window.location.protocol = 'https:'
    }
  }, [config?.geoFence])

  return (
    <RestrictionContext.Provider
      value={{
        restricted,
        setGeofencingUserApproved: setGeofencingUserApprovedAndCache,
        setCoords,
        geofencingUserApproved,
        showGeoInstructions,
        isLoading,
        netmaskMessage,
      }}
    >
      {config?.geoFence && geofencingUserApproved && <GeoFenceRenderer />}
      { children }
    </RestrictionContext.Provider>
  )
}

// This hook is set up as a component to prevent a "different number of hooks" error when we conditionally render it.
// It is conditionally rendered so that the useGeoLocation hook only runs after the user
// has been notified of what will happen.
const GeoFenceRenderer: React.FC = () => {
  useGeoFence()
  return (
    <></>
  )
}
